import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Header/Navbar';
import Footer from '../components/Footer';
import Button from '../components/Button';
import AdList from '../components/AdList/AdList';
import { listingService } from '../api/services/ListingService';
import { listingViewModelToAdListing } from '../api/utils/listingMapper';
import type { ListingViewModel } from '../api/types/listings/ListingViewModel';
import type { AdListing } from '../types/adListing';
import { ViewOption } from '../types/viewOptions';
import { SortingOptions } from '../types/sortingOptions';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useListingContext } from 'contexts/ListingContext';
import MultiSelectModal from 'components/Modal/MultiSelectModal';
import Modal from 'components/Modal/Modal';
import { useNavigation } from 'react-router-dom';

type StatusFilter = 'all' | 'active' | 'pending' | 'sold' | 'cancelled' | 'expired';

/** Normalize API status name to filter key. */
function statusNameToFilter(name: string | null | undefined): StatusFilter {
  if (!name) return 'all';
  const n = name.toLowerCase();
  if (n.includes('aktif') || n === 'active') return 'active';
  if (n.includes('bekleyen') || n.includes('pending')) return 'pending';
  if (n.includes('satıldı') || n.includes('sold')) return 'sold';
  if (n.includes('iptal') || n.includes('cancel')) return 'cancelled';
  if (n.includes('süresi dol') || n.includes('expired')) return 'expired';
  return 'all';
}

const Ilanlarim: React.FC = () => {
  const navigation = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [rawListings, setRawListings] = useState<ListingViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [viewType, setViewType] = useState<ViewOption>(ViewOption.GRID);
  const [sortOption, setSortOption] = useState<SortingOptions>(SortingOptions.NEWEST);
  const { showDeleteModal, setShowDeleteModal, deletingListingId, setDeletingListingId } = useListingContext();
  const [deleted, setDeleted] = useState(false);

  const fetchListings = useCallback(async (userId: string) => {
    setLoading(true);
    setFetchError(null);
    try {
      const result = await listingService.getByUserId(userId);
      if (result.isSuccess && result.data) {
        setRawListings(result.data);
      } else {
        setRawListings([]);
        setFetchError(result.message ?? 'İlanlar yüklenemedi.');
      }
    } catch (err) {
      setRawListings([]);
      setFetchError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigation('/giris', { replace: true });
      return;
    }
    if (user?.id) {
      fetchListings(user.id);
    }
  }, [isAuthenticated, user?.id, fetchListings, navigation]);

  const filteredByStatus = rawListings.filter((lv) => {
    if (statusFilter === 'all') return true;
    return statusNameToFilter(lv.status?.name ?? null) === statusFilter;
  });

  const sortedListings: AdListing[] = [...filteredByStatus]
    .sort((a, b) => {
      if (sortOption === SortingOptions.PRICE_ASC) return a.price - b.price;
      if (sortOption === SortingOptions.PRICE_DESC) return b.price - a.price;
      return 0;
    })
    .map(listingViewModelToAdListing);

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value as SortingOptions);
  };

  const handleViewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setViewType(e.target.value as ViewOption);
  };

  const handleDelete = async () => {
    if (!deletingListingId) return;

    const result = await listingService.delete(deletingListingId.toString());
    if (result.isSuccess) {
      setShowDeleteModal(false);
      setDeletingListingId(null);
      setDeleted(true);
      navigation('/ilanlarim', { replace: true });
    } else {
      alert(result.message ?? 'İlan silinemedi.');
    }
  }

  const handleRejectDelete = () => {
    setShowDeleteModal(false);
    setDeletingListingId(null);
  }

  if (!isAuthenticated) return null;
  if (!user) {
    return (
      <div className="min-vh-100 bg-light">
        <Navbar />
        <main className="container py-4">
          <div className="text-center py-5 text-muted">Yükleniyor...</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      <Navbar />

      <main className="container py-4">
        <h1 className="mb-4">İlanlarım</h1>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <div className="btn-group">
            <Button
              className={`btn btn-outline-primary ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              Tümü
            </Button>
            <Button
              className={`btn btn-outline-primary ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              Bekleyen İlanlar
            </Button>
            <Button
              className={`btn btn-outline-primary ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              Aktif İlanlar
            </Button>
            <Button
              className={`btn btn-outline-primary ${statusFilter === 'sold' ? 'active' : ''}`}
              onClick={() => setStatusFilter('sold')}
            >
              Satılan İlanlar
            </Button>
            <Button
              className={`btn btn-outline-primary ${statusFilter === 'cancelled' ? 'active' : ''}`}
              onClick={() => setStatusFilter('cancelled')}
            >
              İptal Edilen İlanlar
            </Button>
            <Button
              className={`btn btn-outline-primary ${statusFilter === 'expired' ? 'active' : ''}`}
              onClick={() => setStatusFilter('expired')}
            >
              Süresi Dolan İlanlar
            </Button>
          </div>
          <Link className="btn btn-primary" to="/ilan-ver">
            <i className="fas fa-plus me-2" />Yeni İlan Ver
          </Link>
        </div>

        {fetchError && (
          <div className="alert alert-warning mb-3" role="alert">
            {fetchError}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5 text-muted">Yükleniyor...</div>
        ) : sortedListings.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-box-open fa-3x text-muted mb-3" />
            <h3>Henüz ilanınız yok</h3>
            <p className="text-muted">Hemen yeni bir ilan oluşturarak satışa başlayabilirsiniz.</p>
            <Link className="btn btn-primary" to="/ilan-ver">
              İlan Ver
            </Link>
          </div>
        ) : (
          <AdList
            sortedIlanlar={sortedListings}
            itemsPerPage={sortedListings.length}
            currentPage={1}
            totalPages={1}
            sortOption={sortOption}
            viewType={viewType}
            handleSortChange={handleSortChange}
            handleViewChange={handleViewChange}
            detailLinkBase="/ilan-detay-login"
            displayFavorites={false}
            displayEdit={true}
            displayDelete={true}
          />
        )}

        {showDeleteModal && (
          <MultiSelectModal
            title="İlan Silinecek"
            message="Bu işlemi geri alamazsınız. İlanınızı silmek istediğinize emin misiniz?"
            onAccept={() => handleDelete()}
            onReject={() => handleRejectDelete()} />
        )}

        {deleted && (
          <Modal
            title="İlan Silindi"
            message="İlanınız başarıyla silindi."
            onClose={() => setDeleted(false)}
          />
        )

        }
      </main>

      <Footer />
    </div>
  );
};

export default Ilanlarim;
